import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GlobalSearchResult {
  type: 'animal' | 'facility' | 'permit' | 'enclosure';
  id: string;
  title: string;
  subtitle?: string;
  extra?: string;
}

export interface GlobalSearchParams {
  query: string;
  types?: string[];
  limit?: number;
  offset?: number;
}

export const useGlobalSearch = ({ query, types, limit = 10, offset = 0 }: GlobalSearchParams) => {
  return useQuery({
    queryKey: ['global-search', query, types, limit, offset],
    queryFn: async () => {
      if (!query || query.length < 2) {
        return { items: [], total: 0, limit, offset };
      }

      const typesParam = types?.join(',') || 'animal,facility,permit,enclosure';
      
      // Build URL with query params
      const params = new URLSearchParams({
        q: query,
        types: typesParam,
        limit: limit.toString(),
        offset: offset.toString(),
      });

      console.log('[GlobalSearch] Making request with params:', params.toString());

      // Get the current session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Make direct fetch call since invoke doesn't support GET with query params well
      const SUPABASE_URL = 'https://crpxcsrljmdcrgnedfxb.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNycHhjc3Jsam1kY3JnbmVkZnhiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUwODk4MDEsImV4cCI6MjA3MDY2NTgwMX0.nck8fXjf1Y43T5ji89qKwZVLZGBo-qPgSnl02QHvA-M';
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/global-search?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('[GlobalSearch] Error:', error);
        throw new Error(error.error || 'Search failed');
      }

      const data = await response.json();
      console.log('[GlobalSearch] Success, got data:', data);

      return {
        items: (data?.items || []) as GlobalSearchResult[],
        total: (data?.total || 0) as number,
        limit: data?.limit || limit,
        offset: data?.offset || offset,
      };
    },
    enabled: query.length >= 2,
    staleTime: 0, // No caching during testing
    retry: 1,
  });
};
